export type ExpiryPhase = 'ok' | 'warn' | 'error';
const DAY_MS = 24 * 60 * 60 * 1000;

// Normalizes a Date to midnight (00:00:00) to avoid partial-day differences
export const clampMidnight = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// Checks if a date falls on a weekend (Saturday = 6, Sunday = 0)
export const isWeekend = (d: Date) => { const day = d.getDay(); return day === 0 || day === 6; };

// Adds or subtracts working days (Monday-Friday) from a date, skipping weekends.
// Positive days = add to future, negative days = subtract to past.
export const addWorkingDays = (fromDate: Date, workingDays: number) => {
    const result = clampMidnight(new Date(fromDate));
    const direction = workingDays >= 0 ? 1 : -1;
    let daysToAdd = Math.abs(workingDays);

    while (daysToAdd > 0) {
        result.setDate(result.getDate() + direction);
        if (!isWeekend(result)) {
            daysToAdd--;
        }
    }
    return result;
};

// Counts working days between two dates (excludes weekends).
// Returns positive if toDate is in the future, negative if in the past.
export const workingDaysUntil = (fromDate: Date, toDate: Date) => {
    const from = clampMidnight(new Date(fromDate));
    const to = clampMidnight(new Date(toDate));
    
    if (from.getTime() === to.getTime()) return 0;
    
    // Safety check: if the date range is absurdly large (>100 years), return undefined
    // This prevents infinite loops with invalid/placeholder dates like year 0001
    const daysDiff = Math.abs((to.getTime() - from.getTime()) / DAY_MS);
    if (daysDiff > 36500) { // 100 years
        console.warn(`workingDaysUntil: Date range too large (${daysDiff} days), returning undefined`);
        return undefined;
    }
    
    const direction = to.getTime() > from.getTime() ? 1 : -1;
    let current = new Date(from);
    let count = 0;
    
    while (current.getTime() !== to.getTime()) {
        current.setDate(current.getDate() + direction);
        if (!isWeekend(current)) {
            count++;
        }
    }
    
    return direction * count;
};

// Calculates number of full days between today (at midnight) and a target date.
// Positive = days remaining, negative = days overdue.
export const daysUntil = (d?: Date | string | null) => {
    if (!d) return undefined;
    const target = clampMidnight(new Date(d as any));
    const today = clampMidnight(new Date());
    return Math.ceil((target.getTime() - today.getTime()) / DAY_MS);
};

// Calculates both expiry date and an earlier "renew by" date (expiry - leadDays WORKING days).
// Returns working days until renewBy, calendar days until expiry, and a phase (ok | warn | error).
// - error: already expired
// - ok: still before renew-by
// - warn: past renew-by but not yet expired
export function renewByInfo(leadDays: number, expiry?: Date | string | null) {
    if (!expiry) return { dateText: 'n/a', daysFromRenewBy: undefined, phase: 'ok' as ExpiryPhase };

    const e = clampMidnight(new Date(expiry as any));
    // Calculate renewBy by subtracting working days (not calendar days)
    const renewBy = addWorkingDays(e, -leadDays);

    const today = clampMidnight(new Date());
    // Count working days from today to renewBy date
    const dToRenewBy = workingDaysUntil(today, renewBy);
    const dToExpiry = daysUntil(e);

    const phase: ExpiryPhase =
        typeof dToExpiry === 'number' && dToExpiry < 0 ? 'error'
            : typeof dToRenewBy === 'number' && dToRenewBy >= 0 ? 'ok'
                : 'warn';

    return { date: e, renewBy, dToRenewBy, dToExpiry, phase };
}

// Calculates only expiry-based info (no separate renew-by date).
// Returns days until expiry and a phase (ok | warn | error).
// - error: already expired
// - warn: within leadDays before expiry
// - ok: still outside the leadDays window
export function expiryInfo(leadDays: number, expiry?: Date | string | null) {
    if (!expiry) return { date: undefined, dToExpiry: undefined, phase: 'ok' as ExpiryPhase };
    const e = clampMidnight(new Date(expiry as any));
    const d = daysUntil(e);
    const phase: ExpiryPhase =
        typeof d === 'number' && d < 0 ? 'error'
            : typeof d === 'number' && d < leadDays ? 'warn'
                : 'ok';
    return { date: e, dToExpiry: d, phase };
}

// Maps semantic phase values into Tailwind color class strings.
export const phaseToClass: Record<ExpiryPhase, string> = {
    ok: 'text-green-600',
    warn: 'text-orange-600',
    error: 'text-red-600',
};
